import type { IconButtonProps } from '@mui/material/IconButton';

import { usePopover, useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useOrganisation } from 'src/organisation';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';

import { useMockedUser } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { AccountSettingsDialog } from './account-settings-dialog';

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();
  const profileDetails = useOrganisation();

  const { user } = useMockedUser();

  const accountSettings = useBoolean();

  const renderMenuActions = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{ paper: { sx: { p: 0, width: 200 } }, arrow: { offset: 20 } }}
    >
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography variant="subtitle2" noWrap>
          {profileDetails?.displayName || 'Profile Not Found'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {profileDetails?.email || 'Email Not Found'}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <MenuList sx={{ p: 1, my: 1, '& li': { p: 0 } }}>
        <MenuItem>
          <Button
            onClick={() => {
              accountSettings.onTrue();
              onClose();
            }}
            startIcon={<Iconify icon="material-symbols:settings-outline-rounded" />}
            sx={{ width: '100%', justifyContent: 'flex-start' }}
          >
            Account settings
          </Button>
        </MenuItem>
      </MenuList>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Box sx={{ p: 1 }}>
        <SignOutButton
          size="medium"
          variant="text"
          onClose={onClose}
          sx={{ display: 'block', textAlign: 'left' }}
        />
      </Box>
    </CustomPopover>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={profileDetails?.profilePictureUrl || user?.photoURL}
        displayName={profileDetails?.displayName || 'Profile Not Found'}
        sx={sx}
        {...other}
      />

      {renderMenuActions()}
      <AccountSettingsDialog open={accountSettings.value} onClose={accountSettings.onFalse} />
    </>
  );
}
